<template>
  <div v-if="partners.length" class="user-partner-permissions">
    <div class="section-title">
      <v-icon color="primary" left>mdi-handshake</v-icon>
      <span>Permisos Especiales</span>
    </div>
    <v-alert type="info" text dense class="mb-3">
      Sobre el stock compartido con cada partner, el usuario tiene por defecto los mismos permisos que en su dealer
      (solo en las condiciones que tienen en común). Puedes personalizarlos para este usuario.
    </v-alert>

    <v-card v-for="partner in partners" :key="partner.dealer.id" outlined class="mb-3">
      <v-card-title class="subtitle-1 py-2">
        <v-icon left small color="primary">mdi-store</v-icon>
        Permisos en {{ partner.dealer.name }}
        <v-chip small class="ml-3" :color="partner.customized ? 'orange' : 'grey lighten-1'" dark>
          {{ partner.customized ? 'Personalizados' : 'Los mismos que en su dealer' }}
        </v-chip>
        <v-spacer></v-spacer>
        <v-btn small text color="grey darken-1" v-if="partner.customized && partner.resettable"
          :loading="saving === partner.dealer.id" :disabled="!!saving" @click="reset(partner)">
          <v-icon left small>mdi-restore</v-icon>
          Restablecer
        </v-btn>
        <v-btn small outlined color="primary" class="ml-2" :disabled="!!saving" @click="open(partner)">
          <v-icon left small>mdi-shield-edit</v-icon>
          Configurar
        </v-btn>
      </v-card-title>
      <v-card-text class="pt-0">
        <div v-if="partner.permissions.length" class="d-flex flex-wrap">
          <v-chip v-for="permission in partner.permissions" :key="permission" x-small color="primary" outlined class="ma-1">
            {{ label(permission) }}
          </v-chip>
        </div>
        <span v-else class="text-caption grey--text text--darken-1">Sin permisos sobre este partner</span>
      </v-card-text>
    </v-card>

    <permissions-form
      v-if="editing"
      :visible.sync="dialog"
      :value="editing.permissions"
      @input="save"
      @update:visible="onVisible"
    ></permissions-form>
  </div>
</template>

<script>
import UserService from '@/services/UserService'
import { permissionLabel } from '@/grupo'

// Permisos de un usuario sobre cada uno de los partners de su dealer: por defecto los de su dealer,
// personalizables a mano por usuario. Los cambios se guardan al momento.
export default {
  name: 'UserPartnerPermissions',

  components: {
    PermissionsForm: () => import('./PermissionsForm')
  },

  props: {
    userId: { type: [Number, String], required: true }
  },

  data: () => ({
    partners: [],
    editing: null,
    dialog: false,
    saving: null
  }),

  watch: {
    userId () {
      this.load()
    }
  },

  mounted () {
    this.load()
  },

  methods: {
    label: permissionLabel,

    async load () {
      try {
        const { data } = await UserService.getPartnerPermissions(this.userId)
        this.partners = (data.data && data.data.partners) || []
      } catch (error) {
        // Sin acceso o sin partners: el panel simplemente no se muestra
        this.partners = []
      }
    },

    open (partner) {
      this.editing = partner
      this.dialog = true
    },

    onVisible (visible) {
      this.dialog = visible
      if (!visible) this.$nextTick(() => { this.editing = null })
    },

    async save (permissions) {
      const partner = this.editing
      if (!partner) return
      this.saving = partner.dealer.id
      try {
        const { data } = await UserService.setPartnerPermissions({ user_id: this.userId, dealership_id: partner.dealer.id, permissions })
        this.partners = data.data.partners
        this.$toast.success(`Permisos en ${partner.dealer.name} guardados`)
      } catch (error) {
        const message = error.response && error.response.data && error.response.data.data && error.response.data.data.message
        this.$toast.error(message || 'No se pudieron guardar los permisos del partner')
      } finally {
        this.saving = null
      }
    },

    async reset (partner) {
      const ok = await this.$confirm(`¿Restablecer los permisos en ${partner.dealer.name}? Volverán a ser los mismos que tiene el usuario en su dealer.`, { title: 'Restablecer permisos' })
      if (!ok) return
      this.saving = partner.dealer.id
      try {
        const { data } = await UserService.resetPartnerPermissions({ user_id: this.userId, dealership_id: partner.dealer.id })
        this.partners = data.data.partners
        this.$toast.success('Permisos restablecidos')
      } catch (error) {
        this.$toast.error('No se pudieron restablecer los permisos')
      } finally {
        this.saving = null
      }
    }
  }
}
</script>

<style scoped>
.section-title {
  display: flex;
  align-items: center;
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 8px;
  padding-bottom: 8px;
  border-bottom: 1px solid rgba(0, 0, 0, 0.12);
}
</style>
